import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGoogleReCaptcha
} from "react-google-recaptcha-v3";
import {
  GoogleLogin
} from "@react-oauth/google";

const API_URL =
  process.env.REACT_APP_API_URL;

/* ─── icon helpers ─── */

const RefreshIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 4v6h6" />
    <path d="M23 20v-6h-6" />
    <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
  </svg>
);

const MailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const EyeIcon = ({ onClick }) => (
  <svg onClick={onClick} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ cursor: "pointer" }}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = ({ onClick }) => (
  <svg onClick={onClick} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ cursor: "pointer" }}>
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"/>
    <polyline points="12 19 5 12 12 5"/>
  </svg>
);

/* ─── floating file cards on the left panel ─── */

const FILE_CARDS = [
  { format: "PDF", name: "financial_report.pdf", size: "2.4 MB", color: "#EF4444", top: 51, left: 20, rotation: 6 },
  { format: "DOCX", name: "marketing_brief.docx", size: "840 KB", color: "#3B82F6", top: 80, left: 257, rotation: -4 },
  { format: "MP4", name: "promo_explainer.mp4", size: "14.2 MB", color: "#8B5CF6", top: 220, left: 58, rotation: -2 },
  { format: "ZIP", name: "assets_backup.zip", size: "124 MB", color: "#F59E0B", top: 249, left: 280, rotation: 5 },
];

const FloatingCard = ({ format, name, size, color, top, left, rotation }) => (
  <div style={{ ...styles.floatingCard, top, left, transform: `rotate(${rotation}deg)` }}>
    <div style={{ ...styles.formatBadge, backgroundColor: `${color}20` }}>
      <span style={{ ...styles.formatText, color }}>{format}</span>
    </div>
    <div style={styles.fileInfo}>
      <span style={styles.fileName}>{name}</span>
      <span style={styles.fileSize}>{size}</span>
    </div>
    <CheckIcon />
  </div>
);

/* ─── main component ─── */

export default function ConvertProLogin() {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const { executeRecaptcha } =
    useGoogleReCaptcha();

  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [resendStatus, setResendStatus] = useState(""); // "", "sending", "sent"

  const handleGoogleLogin =
    async (credentialResponse) => {
      try {
        const response =
          await fetch(
            `${API_URL}/google-login`,
            {
              method: "POST",
              headers: {
                "Content-Type":
                  "application/json"
              },
              body: JSON.stringify({
                token:
                  credentialResponse.credential
              })
            }
          );

        const data =
          await response.json();

        if (response.ok) {
          localStorage.setItem(
            "currentUser",
            JSON.stringify(data.user)
          );

          const selectedPlan =
            localStorage.getItem(
              "selectedPlan"
            );

          if (selectedPlan) {
            navigate("/pricing");
            return;
          } else {
            navigate("/dashboard");
          }
        }
      } catch (error) {
        console.error(
          "Google Login Error",
          error
        );
      }
    };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const recaptchaToken = await executeRecaptcha("login");

      const response = await fetch(`${API_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          recaptcha_token: recaptchaToken,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem("currentUser", JSON.stringify(data.user));
        const selectedPlan = localStorage.getItem("selectedPlan");
        if (selectedPlan) {
          navigate("/pricing");
        } else {
          navigate("/dashboard");
        }
      } else if (
        response.status === 403 &&
        data.detail?.error === "EMAIL_NOT_VERIFIED"
      ) {
        // User exists but email not verified
        setNeedsVerification(true);
        setVerificationEmail(data.detail.email || email);
      } else {
        alert(data.detail || "Login failed");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleResendVerification = async () => {
    try {
      setResendStatus("sending");
      const response = await fetch(`${API_URL}/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: verificationEmail }),
      });

      if (response.ok) {
        setResendStatus("sent");
      } else {
        setResendStatus("");
        alert("Failed to resend verification email. Please try again.");
      }
    } catch (error) {
      console.error(error);
      setResendStatus("");
    }
  };



  return (
    <div style={styles.container}>
      {/* ── left brand panel ── */}
      <div style={styles.leftPanel}>
        <div style={styles.glowTop} />
        <div style={styles.glowBottom} />

        {/* Clickable logo → navigates to home */}
        <div
          style={styles.brandHeader}
          onClick={() => navigate("/")}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && navigate("/")}
        >
          <div style={styles.logoBox}>
            <RefreshIcon />
          </div>
          <span style={styles.brandName}>ConvertPro</span>
        </div>

        <div style={styles.visualSandbox}>
          {FILE_CARDS.map((card) => (
            <FloatingCard key={card.format} {...card} />
          ))}
        </div>

        <div style={styles.brandFooter}>
          <h2 style={styles.tagline}>Convert any file, anywhere, instantly.</h2>
          <p style={styles.taglineSub}>
            Join thousands of modern teams using ConvertPro to handle secure,
            high-speed document, image, and media transformations in the cloud.
          </p>
        </div>
      </div>

      {/* ── right auth panel ── */}
      <div style={styles.rightPanel}>
        <form style={styles.authCard} onSubmit={handleSubmit}>
          {/* Back to Home link */}
          <button
            type="button"
            style={styles.backToHome}
            onClick={() => navigate("/")}
          >
            <ArrowLeftIcon />
            Back to Home
          </button>

          <div style={styles.authHeader}>
            <h1 style={styles.welcomeTitle}>Welcome back</h1>
            <p style={styles.welcomeSub}>Sign in to your account to continue</p>
          </div>

          <div style={styles.fields}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Email Address</label>
              <div style={styles.inputWrapper}>
                <MailIcon />
                <input
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={styles.input}
                />
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <div style={styles.labelRow}>
                <label style={styles.label}>Password</label>
                <button
                  type="button"
                  style={{
                    ...styles.forgotLink,
                    background: "none",
                    border: "none",
                    cursor: "pointer"
                  }}
                  onClick={() => navigate("/forgotpassword")}
                >
                  Forgot password?
                </button>
              </div>
              <div style={styles.inputWrapper}>
                <LockIcon />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={styles.input}
                />
                {showPassword ? (
                  <EyeOffIcon onClick={() => setShowPassword(false)} />
                ) : (
                  <EyeIcon onClick={() => setShowPassword(true)} />
                )}
              </div>
            </div>
          </div>

          <button type="submit" style={styles.signInButton}>Sign In</button>

          {needsVerification && (
            <div style={styles.verificationAlert}>
              <div style={styles.verificationRow}>
                <div style={styles.verificationIconWrap}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <div style={styles.verificationText}>
                  <span style={styles.verificationTitle}>Verify your email address</span>
                  <span style={styles.verificationDesc}>
                    Your email address hasn't been verified yet. Please check your
                    inbox or resend the verification link.
                  </span>
                </div>
              </div>
              <button
                type="button"
                style={styles.resendButton}
                onClick={handleResendVerification}
                disabled={resendStatus === "sending" || resendStatus === "sent"}
              >
                <MailIcon />
                {resendStatus === "sent"
                  ? "Verification Email Sent ✓"
                  : resendStatus === "sending"
                  ? "Sending..."
                  : "Resend Verification Email"}
              </button>
            </div>
          )}

          <div style={styles.dividerRow}>
            <div style={styles.dividerLine} />
            <span style={styles.dividerText}>or continue with</span>
            <div style={styles.dividerLine} />
          </div>

          <div style={styles.socialRow}>
            <GoogleLogin
              onSuccess={handleGoogleLogin}
              onError={() =>
                console.log(
                  "Google Login Failed"
                )
              }
            />
          </div>

          <p style={styles.authFooter}>
            Don't have an account?{" "}
            <button
              type="button"
              style={{
                ...styles.signUpLink,
                background: "none",
                border: "none",
                cursor: "pointer"
              }}
              onClick={() => navigate("/signup")}
            >
              Sign up
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}

/* ─── styles ─── */

const styles = {
  container: {
    display: "flex",
    width: "100%",
    minHeight: "100vh",
    fontFamily: "'Geist', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    backgroundColor: "#ffffff",
  },
  leftPanel: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    width: "48.9%",
    minHeight: "100vh",
    padding: 80,
    backgroundColor: "#090D16",
    overflow: "hidden",
  },
  glowTop: {
    position: "absolute",
    top: -100,
    left: -100,
    width: 400,
    height: 400,
    borderRadius: "50%",
    background: "#2563EB",
    opacity: 0.15,
    filter: "blur(100px)",
    pointerEvents: "none",
  },
  glowBottom: {
    position: "absolute",
    bottom: -100,
    right: -100,
    width: 500,
    height: 500,
    borderRadius: "50%",
    background: "#8B5CF6",
    opacity: 0.1,
    filter: "blur(100px)",
    pointerEvents: "none",
  },
  brandHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    zIndex: 1,
    cursor: "pointer",
    width: "fit-content",
  },
  logoBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: "#2563EB",
  },
  brandName: {
    fontFamily: "'Outfit', sans-serif",
    fontSize: 24,
    fontWeight: 700,
    color: "#ffffff",
  },
  visualSandbox: {
    position: "relative",
    width: "100%",
    height: 320,
    marginTop: 40,
    zIndex: 1,
  },
  floatingCard: {
    position: "absolute",
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    backgroundColor: "#121829",
    border: "1px solid #1E293B",
    borderRadius: 12,
    boxShadow: "0 8px 16px rgba(0,0,0,0.3)",
  },
  formatBadge: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    borderRadius: 6,
  },
  formatText: {
    fontFamily: "'Outfit', sans-serif",
    fontSize: 11,
    fontWeight: 800,
  },
  fileInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  fileName: {
    fontSize: 12,
    fontWeight: 600,
    color: "#ffffff",
  },
  fileSize: {
    fontSize: 10,
    fontWeight: 400,
    color: "#6B7280",
  },
  brandFooter: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
    zIndex: 1,
  },
  tagline: {
    fontFamily: "'Outfit', sans-serif",
    fontSize: 36,
    fontWeight: 600,
    lineHeight: 1.25,
    color: "#ffffff",
    margin: 0,
  },
  taglineSub: {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: 1.6,
    color: "#6B7280",
    margin: 0,
  },
  rightPanel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    padding: 96,
    backgroundColor: "#ffffff",
  },
  authCard: {
    display: "flex",
    flexDirection: "column",
    gap: 40,
    width: 420,
  },
  backToHome: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    fontWeight: 500,
    color: "#64748B",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    fontFamily: "'Geist', 'Inter', -apple-system, sans-serif",
    alignSelf: "flex-start",
    marginBottom: -16,
    transition: "color 0.2s",
  },
  authHeader: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  welcomeTitle: {
    fontFamily: "'Outfit', sans-serif",
    fontSize: 32,
    fontWeight: 700,
    letterSpacing: -0.5,
    color: "#111827",
    margin: 0,
  },
  welcomeSub: {
    fontSize: 16,
    fontWeight: 400,
    color: "#6B7280",
    margin: 0,
  },
  fields: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  labelRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  label: {
    fontSize: 14,
    fontWeight: 500,
    color: "#374151",
  },
  forgotLink: {
    fontSize: 14,
    fontWeight: 500,
    color: "#2563EB",
    textDecoration: "none",
  },
  inputWrapper: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    height: 44,
    padding: "0 14px",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    backgroundColor: "#ffffff",
  },
  input: {
    flex: 1,
    border: "none",
    outline: "none",
    fontSize: 14,
    fontWeight: 400,
    color: "#111827",
    backgroundColor: "transparent",
    fontFamily: "'Geist', 'Inter', -apple-system, sans-serif",
  },
  signInButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: 52,
    border: "none",
    borderRadius: 8,
    backgroundColor: "#2563EB",
    color: "#ffffff",
    fontSize: 16,
    fontWeight: 600,
    fontFamily: "'Geist', 'Inter', -apple-system, sans-serif",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(37,99,235,0.2)",
  },
  dividerRow: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: "#E5E7EB",
  },
  dividerText: {
    fontSize: 12,
    fontWeight: 500,
    color: "#6B7280",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  socialRow: {
    display: "flex",
    gap: 16,
    flexWrap: "wrap",
  },
  socialButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    flex: 1,
    minWidth: 70,
    height: 48,
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    backgroundColor: "#ffffff",
    padding: "0 8px",
  },
  socialIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 18,
    height: 18,
  },
  socialIconPlaceholder: {
    width: 16,
    height: 16,
    borderRadius: "50%",
    backgroundColor: "#E5E7EB",
  },
  socialLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: "#374151",
  },
  authFooter: {
    fontSize: 14,
    fontWeight: 400,
    color: "#6B7280",
    textAlign: "center",
    margin: 0,
  },
  signUpLink: {
    fontWeight: 600,
    color: "#2563EB",
    textDecoration: "none",
  },
verificationAlert: {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: 16,
  backgroundColor: "#FFFBEB",
  border: "1px solid #FDE68A",
  borderRadius: 10,
},
verificationRow: {
  display: "flex",
  gap: 12,
  alignItems: "flex-start",
},
verificationIconWrap: {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  backgroundColor: "#FEF3C7",
  borderRadius: 8,
  flexShrink: 0,
},
verificationText: {
  display: "flex",
  flexDirection: "column",
  gap: 4,
},
verificationTitle: {
  fontSize: 14,
  fontWeight: 600,
  color: "#92400E",
},
verificationDesc: {
  fontSize: 14,
  lineHeight: 1.5,
  color: "#B45309",
},
resendButton: {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  width: "100%",
  height: 44,
  backgroundColor: "#FFFFFF",
  border: "1px solid #FCD34D",
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 600,
  color: "#B45309",
  cursor: "pointer",
  fontFamily: "'Geist', 'Inter', -apple-system, sans-serif",
},
};
